import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Search, Megaphone, BarChart3, Globe, MessageSquare, TrendingUp, ChevronRight, Pause, Play, Zap } from "lucide-react";
import { CAMPAIGNS } from "@/data/dummyData";

const TYPE_ICONS = { meta: Megaphone, google: Globe, whatsapp: MessageSquare, landing_page: BarChart3 };
const TYPE_COLORS = { meta: "bg-blue-50 text-blue-700 border-blue-200", google: "bg-red-50 text-red-700 border-red-200", whatsapp: "bg-green-50 text-green-700 border-green-200", landing_page: "bg-purple-50 text-purple-700 border-purple-200" };
const TYPE_LABELS = { meta: "Meta Ads", google: "Google Ads", whatsapp: "WhatsApp", landing_page: "Landing Page" };

const STEPS = ["Campaign Type", "Property", "Budget & Target", "Launch"];

function ScoreBadge({ score }) {
  const color = score >= 80 ? "text-emerald-700 bg-emerald-50" : score >= 60 ? "text-blue-700 bg-blue-50" : "text-yellow-700 bg-yellow-50";
  return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${color}`}>{score}</span>;
}

function CreateCampaignModal({ onClose }) {
  const [step, setStep] = useState(0);
  const [type, setType] = useState(null);
  const [property, setProperty] = useState(null);

  const PROPERTIES_LIST = ["Skyline Residences", "Palm Gardens Villa", "The Grand Penthouse", "Marina Bay Towers", "Heritage Heights", "Royal Enclave"];
  const TYPES = ["meta", "google", "whatsapp", "landing_page"];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl"
        onClick={e => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        data-testid="create-campaign-modal"
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-slate-900 text-lg">Create New Campaign</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl font-light w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100">×</button>
          </div>
          {/* Steps */}
          <div className="flex items-center gap-1">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-1 flex-1">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${i <= step ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-400"}`}>{i + 1}</div>
                <span className={`text-xs font-medium flex-1 ${i <= step ? "text-slate-700" : "text-slate-400"}`}>{s}</span>
                {i < STEPS.length - 1 && <div className={`w-4 h-px ${i < step ? "bg-blue-600" : "bg-slate-200"}`} />}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="p-6 min-h-56">
          {step === 0 && (
            <div>
              <p className="text-sm text-slate-500 mb-4">Select the campaign channel</p>
              <div className="grid grid-cols-2 gap-3">
                {TYPES.map(t => {
                  const Icon = TYPE_ICONS[t];
                  return (
                    <button
                      key={t}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${type === t ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
                      onClick={() => setType(t)}
                      data-testid={`campaign-type-${t}`}
                    >
                      <Icon className={`w-5 h-5 mb-2 ${type === t ? "text-blue-600" : "text-slate-500"}`} />
                      <p className={`text-sm font-semibold ${type === t ? "text-blue-700" : "text-slate-700"}`}>{TYPE_LABELS[t]}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <p className="text-sm text-slate-500 mb-4">Select a property for this campaign</p>
              <div className="grid grid-cols-2 gap-3">
                {PROPERTIES_LIST.map(p => (
                  <button
                    key={p}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${property === p ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
                    onClick={() => setProperty(p)}
                    data-testid={`property-select-${p.replace(/\s/g, "-").toLowerCase()}`}
                  >
                    <p className={`text-sm font-semibold ${property === p ? "text-blue-700" : "text-slate-700"}`}>{p}</p>
                    <p className="text-xs text-slate-400 mt-1">Elite Estate</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1">Daily Budget (₹)</label>
                <input type="number" defaultValue={5000} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1">Target Audience</label>
                <input type="text" defaultValue="Mumbai, Age 28-45, HNI" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-1">Campaign Duration</label>
                <select className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300">
                  <option>30 days</option>
                  <option>60 days</option>
                  <option>90 days</option>
                  <option>Ongoing</option>
                </select>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="font-heading font-bold text-slate-900 text-lg mb-2">Campaign Ready to Launch!</h4>
              <p className="text-slate-500 text-sm mb-4">Your campaign for <strong>{property || "selected property"}</strong> via <strong>{TYPE_LABELS[type] || "selected channel"}</strong> is configured.</p>
              <div className="bg-slate-50 rounded-xl p-4 text-left">
                <div className="flex justify-between text-sm mb-2"><span className="text-slate-500">Property</span><span className="font-semibold">{property || "—"}</span></div>
                <div className="flex justify-between text-sm mb-2"><span className="text-slate-500">Channel</span><span className="font-semibold">{TYPE_LABELS[type] || "—"}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">AI Score</span><span className="font-semibold text-blue-600">—</span></div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100 flex justify-between">
          <button
            onClick={step === 0 ? onClose : () => setStep(s => s - 1)}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            {step === 0 ? "Cancel" : "Back"}
          </button>
          <button
            onClick={step < STEPS.length - 1 ? () => setStep(s => s + 1) : onClose}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2"
            data-testid="campaign-next-btn"
          >
            {step === STEPS.length - 1 ? "Launch Campaign" : "Continue"} <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default function Campaigns() {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const FILTERS = ["all", "meta", "google", "whatsapp", "landing_page"];

  const filtered = CAMPAIGNS.filter(c => {
    const matchType = filter === "all" || c.type === filter;
    const matchSearch = c.name.toLowerCase().includes(search.toLowerCase()) || c.property.toLowerCase().includes(search.toLowerCase());
    return matchType && matchSearch;
  });

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Campaigns</h2>
          <p className="text-slate-500 text-sm mt-0.5">{CAMPAIGNS.filter(c => c.status === "active").length} active · {CAMPAIGNS.length} total</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
          data-testid="create-campaign-btn"
        >
          <Plus className="w-4 h-4" /> New Campaign
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Leads", value: CAMPAIGNS.reduce((s, c) => s + c.leads, 0).toLocaleString(), color: "text-blue-600" },
          { label: "Total Spent", value: `₹${(CAMPAIGNS.reduce((s, c) => s + c.totalSpent, 0) / 100000).toFixed(1)}L`, color: "text-slate-800" },
          { label: "Avg. CPL", value: `₹${Math.round(CAMPAIGNS.reduce((s, c) => s + c.cpl, 0) / CAMPAIGNS.length)}`, color: "text-orange-600" },
          { label: "Avg. ROAS", value: (CAMPAIGNS.reduce((s, c) => s + c.roas, 0) / CAMPAIGNS.length).toFixed(1) + "x", color: "text-emerald-600" },
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-xs text-slate-500 mb-1">{stat.label}</p>
            <p className={`font-heading text-2xl font-bold ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Filters + Search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${filter === f ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              data-testid={`filter-${f}`}
            >
              {f === "all" ? "All" : TYPE_LABELS[f]}
            </button>
          ))}
        </div>
        <div className="relative sm:ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search campaigns..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 w-56"
            data-testid="campaign-search"
          />
        </div>
      </div>

      {/* Campaign Cards */}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
        {filtered.map((camp, i) => {
          const Icon = TYPE_ICONS[camp.type];
          return (
            <motion.div
              key={camp.id}
              className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-all group"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
              data-testid={`campaign-card-${camp.id}`}
            >
              {/* Card Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${TYPE_COLORS[camp.type]}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 leading-tight">{camp.name}</p>
                    <p className="text-xs text-slate-500">{camp.property}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${camp.status === "active" ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-700"}`}>
                    {camp.status}
                  </span>
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="text-center p-2 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-400">Leads</p>
                  <p className="font-heading font-bold text-slate-800 text-sm">{camp.leads}</p>
                </div>
                <div className="text-center p-2 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-400">CPL</p>
                  <p className="font-heading font-bold text-slate-800 text-sm">₹{camp.cpl}</p>
                </div>
                <div className="text-center p-2 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-400">ROAS</p>
                  <p className="font-heading font-bold text-emerald-700 text-sm">{camp.roas}x</p>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs text-slate-500">Daily ₹{camp.dailyBudget.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5 text-blue-500" />
                  <span className="text-xs font-semibold text-slate-700">AI Score</span>
                  <ScoreBadge score={camp.aiScore} />
                </div>
              </div>

              {/* Bottom Actions */}
              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                <span className="text-xs text-slate-400">Started {camp.startDate}</span>
                <div className="flex items-center gap-2">
                  <button className={`p-1.5 rounded-lg text-xs font-medium transition-colors ${camp.status === "active" ? "text-yellow-600 hover:bg-yellow-50" : "text-green-600 hover:bg-green-50"}`} data-testid={`toggle-campaign-${camp.id}`}>
                    {camp.status === "active" ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                  </button>
                  <button className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-lg text-xs font-medium transition-colors" data-testid={`view-campaign-${camp.id}`}>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {showCreate && <CreateCampaignModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
